// Where a signal's record lives: the app's own page for the data types that
// have one, else the record's external page (a Notion page) when the backend
// gives its URL.

import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import type { MouseEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { Signal } from "../../api/client.ts";

/** The in-app route of a signal's record, if its data type has a page. */
export function recordRoute(dataType: string, recordId: string): string | null {
  switch (dataType) {
    case "job_ad":
      return `/job-ads/${recordId}`;
    case "news_article":
      return `/news-articles/${recordId}`;
    case "tender":
    case "contract_expiry":
      return `/tenders/${recordId}`;
    case "regulatory_filing":
      return `/regulatory-filings/${recordId}`;
    default:
      return null;
  }
}

/** "open" to the record's page, its title off-site, or a dash. */
export default function SignalRecordLink({
  signal,
  onClick,
}: {
  signal: Pick<Signal, "data_type" | "record_id" | "record_title" | "record_url">;
  // E.g. to keep a row's own click from firing.
  onClick?: (e: MouseEvent) => void;
}) {
  const route = recordRoute(signal.data_type, signal.record_id);
  if (route) {
    return (
      <Link component={RouterLink} to={route} underline="hover" onClick={onClick}>
        open
      </Link>
    );
  }
  if (signal.record_url) {
    return (
      <Link
        href={signal.record_url}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={onClick}
      >
        {signal.record_title || "open"}
      </Link>
    );
  }
  return (
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      —
    </Typography>
  );
}
